import { useState, useEffect, useMemo } from 'react';
import { useAuth, type UnitScale, unitLabel, API_BASE } from '../App';
import { authedFetch } from '../supabaseClient';
import {
  LineChart, Line, ScatterChart, Scatter, XAxis, YAxis, ZAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';

// ── Constants ─────────────────────────────────────────────────────────────────

const PROJECT_COLORS = [
  '#3b82f6', '#34d399', '#f59e0b', '#ef4444',
  '#8b5cf6', '#ec4899', '#06b6d4', '#f97316',
];

const KPI_KEYS = [
  'run_rate_revenue',
  'yoy_revenue_growth',
  'gross_margin_pct',
  'ebitda_margin_pct',
  'nrr',
  'grr',
  'burn_multiple',
  'cac',
  'ltv_over_cac',
  'rule_of_40',
  'm6_revenue_retention',
  'm13_revenue_retention',
  'm25_revenue_retention',
  'm6_logo_retention',
  'm13_logo_retention',
  'm25_logo_retention',
  'm6_cum_rev_per_cust',
  'm13_cum_rev_per_cust',
  'm25_cum_rev_per_cust',
];

let _scale = 1000;

function kpiLabel(key: string): string {
  const labels: Record<string, string> = {
    run_rate_revenue:   `Run-Rate Revenue ${unitLabel(_scale as UnitScale)}`,
    yoy_revenue_growth: 'YoY Revenue Growth (%)',
    gross_margin_pct:   'Gross Margin (%)',
    ebitda_margin_pct:  'EBITDA Margin (%)',
    nrr:                'Net Revenue Retention (%)',
    grr:                'Gross Revenue Retention (%)',
    burn_multiple:      'Burn Multiple',
    cac:                `CAC ${unitLabel(_scale as UnitScale)}`,
    ltv_over_cac:       'LTV / CAC',
    rule_of_40:         'Rule of 40',
    m6_revenue_retention:    'M6 Revenue Retention (%)',
    m13_revenue_retention:   'M13 Revenue Retention (%)',
    m25_revenue_retention:   'M25 Revenue Retention (%)',
    m6_logo_retention:       'M6 Customer Retention (%)',
    m13_logo_retention:      'M13 Customer Retention (%)',
    m25_logo_retention:      'M25 Customer Retention (%)',
    m6_cum_rev_per_cust:     `M6 Cumulative Rev / Customer ${unitLabel(_scale as UnitScale)}`,
    m13_cum_rev_per_cust:    `M13 Cumulative Rev / Customer ${unitLabel(_scale as UnitScale)}`,
    m25_cum_rev_per_cust:    `M25 Cumulative Rev / Customer ${unitLabel(_scale as UnitScale)}`,
  };
  return labels[key] || key;
}

// ── Format helpers ────────────────────────────────────────────────────────────

const PCT_KPIS = new Set([
  'yoy_revenue_growth', 'gross_margin_pct', 'ebitda_margin_pct',
  'nrr', 'grr', 'rule_of_40',
  'm6_revenue_retention', 'm13_revenue_retention', 'm25_revenue_retention',
  'm6_logo_retention', 'm13_logo_retention', 'm25_logo_retention',
]);
const MULT_KPIS = new Set(['burn_multiple', 'ltv_over_cac']);
const DOLLAR_KPIS = new Set([
  'run_rate_revenue', 'cac',
  'm6_cum_rev_per_cust', 'm13_cum_rev_per_cust', 'm25_cum_rev_per_cust',
]);

function fmtVal(kpi: string, v: number | undefined | null): string {
  if (v === undefined || v === null) return '—';
  if (PCT_KPIS.has(kpi))    return `${v.toFixed(1)}%`;
  if (MULT_KPIS.has(kpi))   return `${v.toFixed(1)}x`;
  if (DOLLAR_KPIS.has(kpi)) {
    const scaled = v / _scale;
    return scaled < 1 && scaled > -1
      ? `$${scaled.toFixed(1)}`
      : `$${Math.round(scaled).toLocaleString()}`;
  }
  return v.toFixed(1);
}

function fmtAxis(kpi: string, v: number): string {
  if (PCT_KPIS.has(kpi))  return `${v}%`;
  if (MULT_KPIS.has(kpi)) return `${v}x`;
  if (DOLLAR_KPIS.has(kpi)) {
    const scaled = v / _scale;
    const abs = Math.abs(scaled);
    return abs >= 1000 ? `$${Math.round(scaled / 1000)}K` : `$${Math.round(scaled)}`;
  }
  return `${v}`;
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface BenchmarkProject {
  project_id: string;
  name: string;
  stage: string | null;
  sector: string | null;
  business_model: string | null;
  first_revenue_period: string | null;
  pic: string | null;
  period_type: string | null;
  snapshot_date: string | null;
  kpis: Record<string, Record<string, number>>;
}

// ── Helper: month offset arithmetic ──────────────────────────────────────────

function parseYM(s: string): number {
  const [y, m] = s.split('-').map(Number);
  return y * 12 + (m - 1);               // 0-indexed months
}

// YYYY-MM → M/YYYY for display. Matches the app-wide period formatting
// convention used in CategorizationView / FinancialsTable (e.g. "2024-01"
// renders as "1/2024"). Defensive: returns the raw string on malformed
// input rather than throwing, so a bad `first_revenue_period` doesn't
// crash the legend.
function formatPeriod(s: string | null | undefined): string {
  if (!s) return '';
  const [y, m] = s.split('-');
  if (!y || !m) return s;
  const mNum = parseInt(m, 10);
  if (isNaN(mNum)) return s;
  return `${mNum}/${y}`;
}

// ── useIsMobile (matches App.tsx lg breakpoint) ──────────────────────────────

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

// ══════════════════════════════════════════════════════════════════════════════
// BenchmarkingView
// ══════════════════════════════════════════════════════════════════════════════

export default function BenchmarkingView({ unitScale }: { unitScale: UnitScale }) {
  _scale = unitScale;
  const { isAuthenticated, userId } = useAuth();
  const isMobile = useIsMobile();

  // ── Data ──────────────────────────────────────────────────────────────────
  const [benchmarks, setBenchmarks] = useState<BenchmarkProject[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');

  // ── Library panel ─────────────────────────────────────────────────────────
  // Default open on desktop, closed on mobile
  const [libraryOpen, setLibraryOpen] = useState(() =>
    typeof window !== 'undefined' ? !window.matchMedia('(max-width: 1023px)').matches : true
  );
  const [search, setSearch]           = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [modelFilter, setModelFilter] = useState('');
  const [selected, setSelected]       = useState<Set<string>>(new Set());

  // ── KPI section ───────────────────────────────────────────────────────────
  const [activeKpi, setActiveKpi]   = useState('run_rate_revenue');
  const [viewMode, setViewMode]     = useState<'chart' | 'table'>('chart');
  const [scatterView, setScatterView] = useState<'chart' | 'table'>('chart');

  // ── Fetch benchmarks ──────────────────────────────────────────────────────

  useEffect(() => {
    setLoading(true);
    setError('');

    authedFetch(`${API_BASE}/api/benchmarks`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => {
        const items: BenchmarkProject[] = data.benchmarks || [];
        setBenchmarks(items);
        setSelected(new Set(items.map(b => b.project_id)));   // auto-select all
      })
      .catch(() => setError('Failed to load benchmarks'))
      .finally(() => setLoading(false));
  }, [isAuthenticated, userId]);

  // ── Derived data ──────────────────────────────────────────────────────────

  const filtered = useMemo(() => benchmarks.filter(b => {
    if (search && !b.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (stageFilter && b.stage !== stageFilter) return false;
    if (modelFilter && b.business_model !== modelFilter) return false;
    return true;
  }), [benchmarks, search, stageFilter, modelFilter]);

  const selectedProjects = useMemo(
    () => benchmarks.filter(b => selected.has(b.project_id)),
    [benchmarks, selected],
  );

  const colorMap = useMemo(() => {
    const m: Record<string, string> = {};
    selectedProjects.forEach((p, i) => {
      m[p.project_id] = PROJECT_COLORS[i % PROJECT_COLORS.length];
    });
    return m;
  }, [selectedProjects]);

  const stages = useMemo(
    () => Array.from(new Set(benchmarks.map(b => b.stage).filter((s): s is string => !!s))),
    [benchmarks],
  );
  const models = useMemo(
    () => Array.from(new Set(benchmarks.map(b => b.business_model).filter((s): s is string => !!s))),
    [benchmarks],
  );

  // ── Selection helpers ─────────────────────────────────────────────────────

  const toggle = (pid: string) =>
    setSelected(prev => {
      const next = new Set(prev);
      next.has(pid) ? next.delete(pid) : next.add(pid);
      return next;
    });

  const selectAll  = () => setSelected(new Set(filtered.map(b => b.project_id)));
  const selectNone = () => setSelected(new Set());

  // ── KPI chart data ────────────────────────────────────────────────────────

  const kpiChartData = useMemo(() => {
    if (!selectedProjects.length) return [];
    const periods = new Set<string>();
    selectedProjects.forEach(p => {
      const d = p.kpis?.[activeKpi];
      if (d) Object.keys(d).forEach(k => periods.add(k));
    });
    return Array.from(periods).sort().map(period => {
      const pt: Record<string, any> = { period };
      selectedProjects.forEach(p => {
        const v = p.kpis?.[activeKpi]?.[period];
        if (v !== undefined && v !== null) pt[p.project_id] = v;
      });
      return pt;
    });
  }, [selectedProjects, activeKpi]);

  // ── KPI table periods ─────────────────────────────────────────────────────

  const kpiTablePeriods = useMemo(() => {
    const s = new Set<string>();
    selectedProjects.forEach(p => {
      const d = p.kpis?.[activeKpi];
      if (d) Object.keys(d).forEach(k => s.add(k));
    });
    return Array.from(s).sort();
  }, [selectedProjects, activeKpi]);

  // ── Growth vs Efficiency scatter data ────────────────────────────────────────

  interface ScatterPoint {
    project_id: string;
    name: string;
    stage: string | null;
    growth: number;
    margin: number;
    rule_of_40: number;
  }

  const scatterData = useMemo((): ScatterPoint[] => {
    return selectedProjects
      .map(p => {
        const growthData = p.kpis?.yoy_revenue_growth;
        const marginData = p.kpis?.ebitda_margin_pct;
        if (!growthData || !marginData) return null;

        // Get latest period value for each
        const growthPeriods = Object.keys(growthData).sort();
        const marginPeriods = Object.keys(marginData).sort();
        if (!growthPeriods.length || !marginPeriods.length) return null;

        const growth = growthData[growthPeriods[growthPeriods.length - 1]];
        const margin = marginData[marginPeriods[marginPeriods.length - 1]];

        return {
          project_id: p.project_id,
          name: p.name,
          stage: p.stage,
          growth: Math.round(growth * 10) / 10,
          margin: Math.round(margin * 10) / 10,
          rule_of_40: Math.round((growth + margin) * 10) / 10,
        };
      })
      .filter((d): d is ScatterPoint => d !== null);
  }, [selectedProjects]);

  // Rule of 40 reference line points (margin = 40 - growth)
  const ro40Line = useMemo(() => {
    if (!scatterData.length) return [];
    const growths = scatterData.map(d => d.growth);
    const margins = scatterData.map(d => d.margin);
    const minX = Math.min(...growths, 0) - 20;
    const maxX = Math.max(...growths) + 20;
    return [
      { growth: minX, margin: 40 - minX },
      { growth: maxX, margin: 40 - maxX },
    ];
  }, [scatterData]);

  // ── Time-zero data ────────────────────────────────────────────────────────

  const tzEligible = useMemo(
    () => selectedProjects.filter(p => p.first_revenue_period && p.kpis?.run_rate_revenue),
    [selectedProjects],
  );

  const timeZeroData = useMemo(() => {
    if (!tzEligible.length) return [];

    // Gather all offsets per project
    type ProjOffsets = { pid: string; offsets: Map<number, number> };
    const all: ProjOffsets[] = [];
    let maxOff = 0;

    for (const p of tzEligible) {
      const t0 = parseYM(p.first_revenue_period!);
      const offsets = new Map<number, number>();
      for (const [period, val] of Object.entries(p.kpis.run_rate_revenue)) {
        const off = parseYM(period) - t0;
        if (off >= 0) {
          offsets.set(off, val);
          if (off > maxOff) maxOff = off;
        }
      }
      all.push({ pid: p.project_id, offsets });
    }

    // Build chart points: only months where at least one project has data
    const points: Record<string, any>[] = [];

    for (let off = 0; off <= maxOff; off++) {
      const pt: Record<string, any> = { month: off };
      let any = false;
      for (const { pid, offsets } of all) {
        const v = offsets.get(off);
        if (v !== undefined) { pt[pid] = v; any = true; }
      }
      if (any) points.push(pt);
    }
    return points;
  }, [tzEligible]);

  // Library panel inner content — shared between mobile overlay and desktop aside
  const libraryContent = (
    <>
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
        <span className="text-xs font-semibold text-white tracking-wide">
          Analysis Library
        </span>
        <button
          onClick={() => setLibraryOpen(false)}
          className="text-gray-500 hover:text-white text-xs transition-colors"
          title="Collapse library"
        >◀</button>
      </div>

      {/* Search */}
      <div className="px-3 py-2">
        <input
          type="text"
          placeholder="Search projects…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full bg-gray-800 border border-gray-700 rounded px-2.5 py-1.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
        />
      </div>

      {/* Filters */}
      <div className="px-3 pb-2 flex gap-2">
        <select
          value={stageFilter}
          onChange={e => setStageFilter(e.target.value)}
          className="flex-1 min-w-0 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-gray-300 focus:outline-none focus:border-blue-500"
        >
          <option value="">All Stages</option>
          {stages.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={modelFilter}
          onChange={e => setModelFilter(e.target.value)}
          className="flex-1 min-w-0 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-gray-300 focus:outline-none focus:border-blue-500"
        >
          <option value="">All Models</option>
          {models.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>

      {/* Select all / none + count */}
      <div className="px-3 pb-2 flex items-center justify-between">
        <div className="flex gap-2 text-xs">
          <button onClick={selectAll} className="text-blue-400 hover:text-blue-300 transition-colors">All</button>
          <span className="text-gray-700">·</span>
          <button onClick={selectNone} className="text-gray-500 hover:text-white transition-colors">None</button>
        </div>
        <span className="text-[10px] text-gray-500">{selected.size} selected</span>
      </div>

      {/* Project cards (scrollable) */}
      <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-1.5">
        {loading && (
          <p className="text-xs text-gray-500 px-1 py-4">Loading…</p>
        )}
        {!loading && filtered.length === 0 && (
          <p className="text-xs text-gray-600 px-1 py-4 leading-relaxed">
            {benchmarks.length === 0
              ? 'No projects in library. Enable benchmarking on the Upload tab.'
              : 'No matches.'}
          </p>
        )}

        {filtered.map(b => {
          const isSel = selected.has(b.project_id);
          const color = colorMap[b.project_id];
          return (
            <button
              key={b.project_id}
              onClick={() => toggle(b.project_id)}
              className={`w-full text-left px-3 py-2 rounded-md border transition-colors ${
                isSel
                  ? 'border-blue-500/40 bg-blue-950/30'
                  : 'border-gray-800 hover:border-gray-700'
              }`}
            >
              <div className="flex items-center gap-2">
                {isSel && color && (
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                )}
                <span className="text-xs font-medium text-white truncate">{b.name}</span>
              </div>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {b.stage && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-800 text-gray-400">
                    {b.stage}
                  </span>
                )}
                {b.business_model && (
                  <span className="text-[10px] text-gray-500">{b.business_model}</span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════════

  return (
    <div className="flex h-full">

      {/* ── Mobile library overlay ────────────────────────────────────────── */}
      {isMobile && libraryOpen && (
        <div
          className="fixed inset-0 z-40 flex"
          style={{ paddingLeft: 'env(safe-area-inset-left, 0px)' }}
        >
          <div
            className="fixed inset-0 bg-black/60"
            onClick={() => setLibraryOpen(false)}
          />
          <aside className="relative w-64 bg-gray-900 border-r border-gray-800 flex flex-col h-full z-50 overflow-y-auto">
            {libraryContent}
          </aside>
        </div>
      )}

      {/* ── Desktop library panel ─────────────────────────────────────────── */}
      {!isMobile && libraryOpen && (
        <aside className="w-60 bg-gray-900 border-r border-gray-800 flex flex-col flex-shrink-0">
          {libraryContent}
        </aside>
      )}

      {/* ── Collapsed toggle strip (both mobile and desktop) ──────────────── */}
      {!libraryOpen && (
        <button
          onClick={() => setLibraryOpen(true)}
          className="w-8 bg-gray-900 border-r border-gray-800 flex items-center justify-center text-gray-500 hover:text-white transition-colors flex-shrink-0"
          title="Expand library"
        >▶</button>
      )}

      {/* ── Main Content ───────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-8">

        {error && <p className="text-red-400 text-sm">{error}</p>}

        {/* Empty state */}
        {!loading && selectedProjects.length === 0 && (
          <div className="flex items-center justify-center h-48">
            <p className="text-gray-600 text-sm">
              {benchmarks.length === 0
                ? 'No projects available for benchmarking.'
                : 'Select projects from the library to compare.'}
            </p>
          </div>
        )}

        {selectedProjects.length > 0 && (
          <>
            {/* ─────────────────────────────────────────────────────────────
                Section 1: KPI Benchmarks
            ───────────────────────────────────────────────────────────── */}
            <section>
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-4">
                <h2 className="text-sm font-bold text-white">KPI Benchmarks</h2>
                <div className="flex items-center gap-3">
                  <select
                    value={activeKpi}
                    onChange={e => setActiveKpi(e.target.value)}
                    className="bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-xs text-gray-200 focus:outline-none focus:border-blue-500"
                  >
                    {KPI_KEYS.map(k => (
                      <option key={k} value={k}>{kpiLabel(k)}</option>
                    ))}
                  </select>
                  <div className="flex border border-gray-700 rounded overflow-hidden">
                    <button
                      onClick={() => setViewMode('chart')}
                      className={`px-3 py-1 text-xs transition-colors ${
                        viewMode === 'chart'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-800 text-gray-400 hover:text-white'
                      }`}
                    >Chart</button>
                    <button
                      onClick={() => setViewMode('table')}
                      className={`px-3 py-1 text-xs transition-colors ${
                        viewMode === 'table'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-800 text-gray-400 hover:text-white'
                      }`}
                    >Table</button>
                  </div>
                </div>
              </div>

              {/* ── Chart view ──────────────────────────────────────────── */}
              {viewMode === 'chart' && (
                kpiChartData.length > 0 ? (
                  <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                    {/* Legend */}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mb-3">
                      {selectedProjects.map(p => (
                        <div key={p.project_id} className="flex items-center gap-1.5">
                          <span
                            className="w-3 h-[3px] rounded-full inline-block"
                            style={{ backgroundColor: colorMap[p.project_id] }}
                          />
                          <span className="text-[10px] text-gray-400">{p.name}</span>
                        </div>
                      ))}
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={kpiChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                        <XAxis
                          dataKey="period"
                          tick={{ fill: '#6b7280', fontSize: 10 }}
                          tickLine={false}
                          axisLine={{ stroke: '#374151' }}
                          tickFormatter={formatPeriod} 
                        />
                        <YAxis
                          tick={{ fill: '#6b7280', fontSize: 10 }}
                          tickLine={false}
                          axisLine={{ stroke: '#374151' }}
                          tickFormatter={v => fmtAxis(activeKpi, v)}
                          width={60}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#111827',
                            border: '1px solid #374151',
                            borderRadius: 8,
                            fontSize: 11,
                          }}
                          labelStyle={{ color: '#9ca3af' }}
                          formatter={(val: any, name: any) => {
                            const proj = selectedProjects.find(p => p.project_id === name);
                            return [fmtVal(activeKpi, Number(val)), proj?.name ?? name];
                          }}
                        />
                        {selectedProjects.map(p => (
                          <Line
                            key={p.project_id}
                            dataKey={p.project_id}
                            stroke={colorMap[p.project_id]}
                            strokeWidth={2}
                            dot={{ r: 2 }}
                            connectNulls
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="text-gray-600 text-xs py-4">
                    No data available for {kpiLabel(activeKpi)}.
                  </p>
                )
              )}

              {/* ── Table view ─────────────────────────────────────────── */}
              {viewMode === 'table' && (
                kpiTablePeriods.length > 0 ? (
                  <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-[11px] md:text-xs">
                        <thead>
                          <tr className="border-b border-gray-800">
                            <th className="sticky left-0 z-10 bg-gray-900 text-left px-2 md:px-4 py-2.5 text-gray-400 font-medium min-w-[120px] md:min-w-[160px]">
                              Project
                            </th>
                            {kpiTablePeriods.map(p => (
                              <th key={p} className="text-right px-2 md:px-3 py-2.5 text-gray-400 font-medium whitespace-nowrap min-w-[64px] md:min-w-[80px]">
                                {p}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {selectedProjects.map(proj => (
                            <tr key={proj.project_id} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                              <td className="sticky left-0 z-10 bg-gray-900 px-2 md:px-4 py-2 font-medium text-white">
                                <div className="flex items-center gap-2">
                                  <span
                                    className="w-2 h-2 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: colorMap[proj.project_id] }}
                                  />
                                  <span className="truncate">{proj.name}</span>
                                </div>
                              </td>
                              {kpiTablePeriods.map(period => (
                                <td key={period} className="text-right px-2 md:px-3 py-2 text-gray-300 whitespace-nowrap tabular-nums">
                                  {fmtVal(activeKpi, proj.kpis?.[activeKpi]?.[period])}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-600 text-xs py-4">
                    No data available for {kpiLabel(activeKpi)}.
                  </p>
                )
              )}
            </section>

            {/* ─────────────────────────────────────────────────────────────
                Section 2: Growth vs. Efficiency
            ───────────────────────────────────────────────────────────── */}
            <section>
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-4">
                <h2 className="text-sm font-bold text-white">Growth vs. Efficiency</h2>
                <div className="flex border border-gray-700 rounded overflow-hidden">
                  <button
                    onClick={() => setScatterView('chart')}
                    className={`px-3 py-1 text-xs transition-colors ${
                      scatterView === 'chart'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-800 text-gray-400 hover:text-white'
                    }`}
                  >Chart</button>
                  <button
                    onClick={() => setScatterView('table')}
                    className={`px-3 py-1 text-xs transition-colors ${
                      scatterView === 'table'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-800 text-gray-400 hover:text-white'
                    }`}
                  >Table</button>
                </div>
              </div>

              {scatterData.length === 0 ? (
                <p className="text-gray-600 text-xs py-4">
                  No selected projects have both YoY Growth and EBITDA Margin data.
                </p>
              ) : scatterView === 'chart' ? (
                <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-3">
                    Latest period. Projects above the line exceed the Rule of 40 (YoY Growth + EBITDA Margin ≥ 40).
                  </p>

                  {/* Legend */}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mb-3">
                    {scatterData.map(d => (
                      <div key={d.project_id} className="flex items-center gap-1.5">
                        <span
                          className="w-2 h-2 rounded-full inline-block"
                          style={{ backgroundColor: colorMap[d.project_id] }}
                        />
                        <span className="text-[10px] text-gray-400">{d.name}</span>
                      </div>
                    ))}
                    <div className="flex items-center gap-1.5">
                      <span className="w-4 h-[1px] inline-block border-t border-dashed border-gray-500" />
                      <span className="text-[10px] text-gray-500">Rule of 40</span>
                    </div>
                  </div>

                  <ResponsiveContainer width="100%" height={350}>
                    <ScatterChart margin={{ top: 10, right: 30, bottom: 10, left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                      <XAxis
                        type="number"
                        dataKey="growth"
                        name="YoY Growth"
                        tick={{ fill: '#6b7280', fontSize: 10 }}
                        tickLine={false}
                        axisLine={{ stroke: '#374151' }}
                        tickFormatter={v => `${v}%`}
                        label={{
                          value: 'YoY Revenue Growth (%)',
                          position: 'insideBottom',
                          offset: -5,
                          fill: '#6b7280',
                          fontSize: 10,
                        }}
                      />
                      <YAxis
                        type="number"
                        dataKey="margin"
                        name="EBITDA Margin"
                        tick={{ fill: '#6b7280', fontSize: 10 }}
                        tickLine={false}
                        axisLine={{ stroke: '#374151' }}
                        tickFormatter={v => `${v}%`}
                        width={50}
                        label={{
                          value: 'EBITDA Margin (%)',
                          angle: -90,
                          position: 'insideLeft',
                          offset: 10,
                          fill: '#6b7280',
                          fontSize: 10,
                        }}
                      />
                      <ZAxis range={[80, 80]} />
                      <Tooltip
                        cursor={{ strokeDasharray: '3 3', stroke: '#374151' }}
                        content={({ active, payload }) => {
                          if (!active || !payload || !payload.length) return null;
                          const d = payload[0]?.payload;
                          if (!d?.name) return null;
                          return (
                            <div className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-xs shadow-lg">
                              <p className="text-white font-medium mb-1">{d.name}</p>
                              <p className="text-gray-400">Growth: {d.growth}%</p>
                              <p className="text-gray-400">Margin: {d.margin}%</p>
                              <p className={`font-medium mt-1 ${d.rule_of_40 >= 40 ? 'text-emerald-400' : 'text-gray-300'}`}>
                                Rule of 40: {d.rule_of_40}
                              </p>
                            </div>
                          );
                        }}
                      />
                      {/* Rule of 40 reference line */}
                      <Scatter
                        data={ro40Line}
                        line={{ stroke: '#6b7280', strokeDasharray: '6 3', strokeWidth: 1 }}
                        shape={() => <></>}
                        legendType="none"
                        isAnimationActive={false}
                      />
                      {/* Project dots */}
                      <Scatter data={scatterData} isAnimationActive={false}>
                        {scatterData.map(d => (
                          <Cell key={d.project_id} fill={colorMap[d.project_id] || '#3b82f6'} />
                        ))}
                      </Scatter>
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                /* Table view */
                <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
                  <table className="w-full text-[11px] md:text-xs">
                    <thead>
                      <tr className="border-b border-gray-800">
                        <th className="text-left px-2 md:px-4 py-2.5 text-gray-400 font-medium">Project</th>
                        <th className="text-left px-2 md:px-3 py-2.5 text-gray-400 font-medium">Stage</th>
                        <th className="text-right px-2 md:px-3 py-2.5 text-gray-400 font-medium">YoY Growth</th>
                        <th className="text-right px-2 md:px-3 py-2.5 text-gray-400 font-medium">EBITDA Margin</th>
                        <th className="text-right px-2 md:px-4 py-2.5 text-gray-400 font-medium">Rule of 40</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...scatterData]
                        .sort((a, b) => b.rule_of_40 - a.rule_of_40)
                        .map(d => (
                          <tr key={d.project_id} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                            <td className="px-2 md:px-4 py-2 font-medium text-white">
                              <div className="flex items-center gap-2">
                                <span
                                  className="w-2 h-2 rounded-full flex-shrink-0"
                                  style={{ backgroundColor: colorMap[d.project_id] }}
                                />
                                {d.name}
                              </div>
                            </td>
                            <td className="px-2 md:px-3 py-2 text-gray-400">{d.stage || '—'}</td>
                            <td className="text-right px-2 md:px-3 py-2 text-gray-300 tabular-nums">{d.growth.toFixed(1)}%</td>
                            <td className="text-right px-2 md:px-3 py-2 text-gray-300 tabular-nums">{d.margin.toFixed(1)}%</td>
                            <td className={`text-right px-2 md:px-4 py-2 font-medium tabular-nums ${d.rule_of_40 >= 40 ? 'text-emerald-400' : 'text-gray-300'}`}>
                              {d.rule_of_40.toFixed(1)}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* ─────────────────────────────────────────────────────────────
                Section 3: Time Zero Analysis
            ───────────────────────────────────────────────────────────── */}
            <section>
              <h2 className="text-sm font-bold text-white mb-4">Time Zero Analysis</h2>

              {tzEligible.length === 0 ? (
                <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
                  <p className="text-gray-500 text-xs leading-relaxed">
                    No selected projects have a First Revenue Period set.
                    Set it on the Upload tab under Project Metadata to align
                    revenue trajectories from T0.
                  </p>
                </div>
              ) : (
                <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-3">
                    Run-Rate Revenue as of the first period generating revenue (month 0)
                  </p>

                  {/* Legend with T0 dates */}
                  <div className="flex flex-wrap gap-x-5 gap-y-1 mb-3">
                    {tzEligible.map(p => (
                      <div key={p.project_id} className="flex items-center gap-1.5">
                        <span
                          className="w-3 h-[3px] rounded-full inline-block"
                          style={{ backgroundColor: colorMap[p.project_id] }}
                        />
                        <span className="text-[10px] text-gray-400">{p.name}</span>
                        <span className="text-[10px] text-gray-600">
                          T0: {formatPeriod(p.first_revenue_period)}
                        </span>
                      </div>
                    ))}
                  </div>

                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={timeZeroData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                      <XAxis
                        dataKey="month"
                        tick={{ fill: '#6b7280', fontSize: 10 }}
                        tickLine={false}
                        axisLine={{ stroke: '#374151' }}
                        label={{
                          value: 'Periods since first revenue',
                          position: 'insideBottom',
                          offset: -5,
                          fill: '#6b7280',
                          fontSize: 10,
                        }}
                      />
                      <YAxis
                        tick={{ fill: '#6b7280', fontSize: 10 }}
                        tickLine={false}
                        axisLine={{ stroke: '#374151' }}
                        tickFormatter={v => {
                          const s = v / _scale;
                          return s >= 1000 ? `$${(s / 1000).toFixed(0)}K` : `$${Math.round(s)}`;
                        }}
                        width={60}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#111827',
                          border: '1px solid #374151',
                          borderRadius: 8,
                          fontSize: 11,
                        }}
                        labelStyle={{ color: '#9ca3af' }}
                        labelFormatter={v => `Month ${v}`}
                        formatter={(val: any, name: any) => {
                          const proj = tzEligible.find(p => p.project_id === name);
                          const scaled = Math.round(Number(val) / _scale);
                          return [`$${scaled.toLocaleString()}`, proj?.name ?? name];
                        }}
                      />
                      {tzEligible.map(p => (
                        <Line
                          key={p.project_id}
                          dataKey={p.project_id}
                          stroke={colorMap[p.project_id]}
                          strokeWidth={2}
                          dot={{ r: 2 }}
                          connectNulls
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </section>

          </>
        )}
      </div>
    </div>
  );
}
